import { observable } from 'mobx';
import { SyncObject } from './SyncObject';

interface ISyncSet<T extends object> {
  insertItem(item: T): void;
  updateItem(currentItem: T, newItem: T);
  deleteItem(item: T): void;

  getSynchronizedItems(): SyncObject<T>[];
  getPendingInserts(): SyncObject<T>[];
  getPendingDeletes(): SyncObject<T>[];
  getPendingUpdates(): SyncObject<T>[];
  getPendingEditsCount(): number;
}

export class SyncSet<T extends object> implements ISyncSet<T> {
  private lastOperation: 'insert' | 'delete' | 'update' | 'none' = 'none';
  private lastEditedItem?: T;
  private lastEditedIndex: any;

  @observable public __syncObjects: SyncObject<T>[] = [];

  constructor(items?: T[]) {
    if (items) {
      this.initializeSet(items);
    }
  }

  private initializeSet(items: T[]) {
    this.__syncObjects = [];
    for (const item of items) {
      const syncObject = SyncObject.constructSynchronized(item);
      this.__syncObjects.push(syncObject);
    }
  }

  private findSyncObject(item: T): SyncObject<T> | undefined {
    let result: SyncObject<T> | undefined;
    for (const syncObject of this.__syncObjects) {
      if (syncObject.syncStatus !== 'pending-delete' && syncObject.current === item) {
        result = syncObject;
        break; //exit for loop
      }
    }
    return result;
  }

  loadItem(item: T): void {
    // console.log('SyncSet.loadItem()');
    if (this.findSyncObject(item)) {
      throw new Error('Invalid operation: Item already exists in this collection.');
    }
    const syncObject = SyncObject.constructSynchronized(item);
    this.__syncObjects.push(syncObject);
  }

  insertItem(item: T): void {
    // console.log('SyncSet.insertItem()');
    if (this.findSyncObject(item)) {
      throw new Error('Invalid operation: Item already exists in this collection.');
    }
    const syncObject = SyncObject.constructInserted(item);
    this.__syncObjects.push(syncObject);
    this.lastOperation = 'insert';
  }

  updateItem(currentItem: T, newItem: T, saveForUndo: boolean = false) {
    const syncObject = this.findSyncObject(currentItem);
    if (syncObject) {
      syncObject.update(newItem);

      // Save for undo
      if (saveForUndo) {
        this.lastEditedItem = currentItem;
        this.lastEditedIndex = this.__syncObjects.indexOf(syncObject);
        this.lastOperation = 'update';
        syncObject.updateEditCount('add');
      }
    } else {
      throw new Error('Invalid operation: item does not exist in the collection.');
    }
  }

  deleteItem(item: T, fromAuto: boolean = false, saveForUndo: boolean = false): void {
    const syncObject = this.findSyncObject(item);
    if (syncObject) {
      if (syncObject.syncStatus === 'pending-insert' || syncObject.syncStatus === 'pending-insert-deleted') {
        if (saveForUndo) {
          syncObject.delete();
          this.lastEditedItem = item;
          this.lastEditedIndex = this.__syncObjects.indexOf(syncObject);
          syncObject.updateEditCount('add');
        } else {
          const index = this.__syncObjects.indexOf(syncObject);
          this.__syncObjects.splice(index, 1);
        }
      } else if (syncObject.syncStatus === 'pending-update' || syncObject.syncStatus === 'synchronized') {
        syncObject.delete();
        // Save for undo
        if (saveForUndo) {
          this.lastEditedItem = item;
          this.lastEditedIndex = this.__syncObjects.indexOf(syncObject);
          syncObject.updateEditCount('add');
        }
      }
      this.lastOperation = fromAuto ? 'none' : 'delete';
      //commenting below code as never ran into this scenario as findSyncObject doesnt consider 'pending-delete'
      //  else {
      //   throw new Error('Unexpected result: syncStatus');
      // }
    }
  }

  deleteLastItem(): void {
    if (this.__syncObjects.length === 0) {
      throw new Error('Cannot delete item: SyncSet is empty.');
    }
    const lastSyncObject = this.__syncObjects[this.__syncObjects.length - 1];
    if (lastSyncObject.syncStatus === 'pending-insert') {
      // Directly remove if not synchronized yet
      this.__syncObjects.pop();
      this.lastOperation = 'delete';
    } else if (lastSyncObject.syncStatus === 'synchronized' || lastSyncObject.syncStatus === 'pending-update') {
      // Mark for deletion if already synchronized or updated
      lastSyncObject.delete();
    }
  }

  undoLastEdit(): void {
    if ((this.lastOperation !== 'update' && this.lastOperation !== 'delete') || !this.lastEditedItem) return;
    const lastSyncObject = this.__syncObjects[this.lastEditedIndex];
    if (lastSyncObject) {
      lastSyncObject.update(this.lastEditedItem);
      this.lastEditedItem = undefined;
      this.lastOperation = 'none';
      lastSyncObject.updateEditCount('subtract');
    }
  }

  getItems(): Readonly<T[]> {
    return this.__syncObjects.filter((so) => so.syncStatus !== 'pending-delete' && so.syncStatus !== 'pending-insert-deleted').map((so) => so.current!);
  }

  // NOTE: DO NOT use @computed when for functions referencing @observable Array types
  // as it causes issues for storybook
  getSynchronizedItems(): SyncObject<T>[] {
    return this.__syncObjects.filter((e) => e.syncStatus === 'synchronized');
  }

  getPendingInserts(): SyncObject<T>[] {
    return this.__syncObjects.filter((e) => e.syncStatus === 'pending-insert');
  }

  getPendingDeletes(): SyncObject<T>[] {
    return this.__syncObjects.filter((e) => e.syncStatus === 'pending-delete');
  }

  getPendingUpdates(): SyncObject<T>[] {
    return this.__syncObjects.filter((e) => e.syncStatus === 'pending-update');
  }

  getPendingEdits(): SyncObject<T>[] {
    return this.__syncObjects.filter((e) => e.syncStatus !== 'synchronized');
  }

  getPendingEditsCount(): number {
    return this.__syncObjects
      .filter(obj => obj.syncStatus !== 'synchronized')
      .reduce((count, obj) => count + obj.pendingEditsCount, 0);
  }

  public getLastOperation(): string {
    return this.lastOperation;
  }
  purgeDeletes() {
    for (const obj of this.getPendingDeletes()) {
      const index = this.__syncObjects.indexOf(obj);
      this.__syncObjects.splice(index, 1);
    }
  }

  clearSyncStatus() {
    this.__syncObjects.forEach((o) => o.acceptChanges());
  }

  //resetSyncSet to clear all the __SyncObjects
  /*
    Added resetSyncSet to avoid the scenario: while reload the drawings after Save, it loads the drawings that are newly proposed along with Saved drawings one more time
     as the Saved drawings and reload of Saved drawings doesn't share the same item id's. 
  */
  resetSyncSet() {
    this.__syncObjects.splice(0, this.__syncObjects.length);
  }

  TEST_loadItem(item: T): void {
    const syncObject = SyncObject.constructSynchronized(item);
    this.__syncObjects.push(syncObject);
  }
}
